// snake_case wire shapes -> camelCase view types for GET /issues/{id} (issue drill-down).

import type { IssueSeverity, IssueStatus } from '@/shared/constants/issue-status'

import type { GisIssueDetail, GisIssueEvent } from './types'

interface RawIssueActor {
  id: string
  display_name: string
  role: string
}

interface RawIssueEvent {
  id: string
  event_type: string
  actor: RawIssueActor | null
  note: string | null
  photo_url: string | null
  photo_urls?: string[] | null
  metadata?: unknown
  created_at: string
}

export interface RawGisIssueDetailResponse {
  id: string
  issue_ref: string
  status: IssueStatus
  severity: IssueSeverity
  title: string
  description: string | null
  photo_url: string | null
  entity_id: string
  site: {
    id: string
    name: string
    entity_id: string
    site_type?: string | null
    assigned_officer?: { id: string; display_name: string; role?: string | null; email?: string | null; phone?: string | null } | null
  } | null
  department: { id: string; name: string; hod: { id: string; display_name: string; email: string; phone: string } | null } | null
  section: { id: string | null; title: string } | null
  survey_ref: string | null
  source_survey: { id: string; survey_ref: string; completed_at: string } | null
  raised_by: { id: string | null; display_name: string } | null
  raised_at: string
  resolved_at: string | null
  resolved_by: string | null
  completed_at: string | null
  events: RawIssueEvent[]
}

/** Coalesce an optional/`undefined` value (from optional chaining) to `null` for the view model. */
const orNull = <T>(v: T | null | undefined): T | null => v ?? null

function mapIssueEvent(e: RawIssueEvent): GisIssueEvent {
  return {
    id: e.id,
    type: e.event_type,
    actorName: orNull(e.actor?.display_name),
    actorRole: orNull(e.actor?.role),
    note: e.note,
    // Backend sends `photo_urls: []` (empty) alongside a populated `photo_url` for single-photo events,
    // so `?? ` isn't enough (an empty array isn't nullish). Use the array only when it has items;
    // otherwise fall back to the single `photo_url`.
    photoUrls: e.photo_urls && e.photo_urls.length > 0 ? e.photo_urls : e.photo_url ? [e.photo_url] : [],
    createdAt: e.created_at,
  }
}

/** HOD contact, destructured once so the main mapper stays flat (avoids deep optional-chain branches). */
function mapHod(hod: { display_name: string; email: string; phone: string } | null | undefined): {
  hodName: string | null
  hodPhone: string | null
  hodEmail: string | null
} {
  return {
    hodName: orNull(hod?.display_name),
    hodPhone: orNull(hod?.phone),
    hodEmail: orNull(hod?.email),
  }
}

/** Site's current in-charge officer (the "Responsibility" FO), id + contact mapped adaptively. The id
 *  matches `GisSite.officerId`, so it opens the officer accountability view. */
function mapOfficer(o: { id?: string; display_name: string; email?: string | null; phone?: string | null } | null | undefined): {
  officerId: string | null
  officerName: string | null
  officerPhone: string | null
  officerEmail: string | null
} {
  return {
    officerId: orNull(o?.id),
    officerName: orNull(o?.display_name),
    officerPhone: orNull(o?.phone),
    officerEmail: orNull(o?.email),
  }
}

export function mapIssueDetail(raw: RawGisIssueDetailResponse): GisIssueDetail {
  return {
    id: raw.id,
    issueRef: raw.issue_ref,
    status: raw.status,
    severity: raw.severity,
    title: raw.title,
    description: raw.description,
    photoUrl: orNull(raw.photo_url),
    siteId: orNull(raw.site?.id),
    siteName: orNull(raw.site?.name),
    departmentName: orNull(raw.department?.name),
    ...mapHod(raw.department?.hod),
    ...mapOfficer(raw.site?.assigned_officer),
    sectionTitle: orNull(raw.section?.title),
    surveyRef: raw.survey_ref,
    raisedByName: orNull(raw.raised_by?.display_name),
    raisedAt: raw.raised_at,
    resolvedAt: raw.resolved_at,
    completedAt: raw.completed_at,
    events: (raw.events ?? []).map(mapIssueEvent),
  }
}
